import { Box, Text } from '@chakra-ui/react'
import { useCallback, useState } from 'react'

import BackgroundCheckQ from '@/queries/BackgroundCheckQ'
import { BackgroundCheckStatus } from '@/queries/constants'
import UserQ from '@/queries/UserQ'
import { useQuery } from '@/client/context/query'
import { useUser } from '@/client/context/user'
import { getCheckrCandidatePortalUrl } from '@/client/services/url/vendor'

import ShwLink from '@/components/ShwLink'
import ModalV3 from '@/components/UI/Modal/ModalV3'

import BackgroundCheckInitiate from './BackgroundCheckInitiate'

export default function BackgroundCheckModal({
  isOpen,
  onClose,
  worldSlug,
}: {
  isOpen: boolean
  onClose: () => void
  worldSlug?: string
}) {
  const query = useQuery()
  const userContext = useUser()

  const user = query.one(UserQ.where({ id: userContext.id }))
  const userCountry = user(UserQ.country) ?? ''
  const canRequestBackgroundCheck = user(UserQ.canGetBackgroundCheck({ worldSlug }))

  const hasCompletedBackgroundCheck = user<boolean>`EXISTS(SELECT 1 ${BackgroundCheckQ.where({
    userId: UserQ.id,
    country: UserQ.country,
    status: BackgroundCheckStatus.completed,
  })})`
  const latestBackgroundCheck = user.one`${BackgroundCheckQ.where({
    userId: UserQ.id,
    country: UserQ.country,
    order: [[BackgroundCheckQ.created_at, 'desc']],
    limit: 1,
  })}`
  const latestBackgroundCheckStatus = latestBackgroundCheck(BackgroundCheckQ.status)
  const [localBackgroundCheckStatus, setLocalBackgroundCheckStatus] =
    useState<BackgroundCheckStatus | null>(null)
  const backgroundCheckStatus = localBackgroundCheckStatus ?? latestBackgroundCheckStatus

  const handleInitiated = useCallback(() => {
    setLocalBackgroundCheckStatus(BackgroundCheckStatus.requested)
  }, [])

  return (
    <ModalV3
      title="Background Check"
      open={isOpen}
      onClose={onClose}
      size="lg"
      body={() => (
        <Box>
          <Text mb="16px">
            When participating in a peer-to-peer learning platform like Schoolhouse, ensuring a safe
            and trustworthy environment is our top priority. As part of our vetting process to
            maintain this standard, we ask that you complete a comprehensive background check. This
            process includes a review of criminal history, verification of identity, and checks
            against national databases.
          </Text>
          <Text mb="16px">
            By implementing these measures, we aim to provide peace of mind to both students and
            parents, knowing that the individuals they interact with are thoroughly vetted and meet
            our high standards of safety and integrity.
          </Text>
          <Text mb="16px">
            At any time, you can use the{' '}
            <ShwLink href={getCheckrCandidatePortalUrl()}>Checkr Candidate Portal</ShwLink> to view
            the status of your background check.
          </Text>
          {hasCompletedBackgroundCheck === true ? (
            <Text color="fg.success" mb="16px">
              Congratulations! Your background check has been completed!
            </Text>
          ) : backgroundCheckStatus === BackgroundCheckStatus.requested ? (
            <Text color="fg.success">
              Please check your email for an invitation from Checkr to submit the required data.
            </Text>
          ) : backgroundCheckStatus === BackgroundCheckStatus.submitted ? (
            <Text color="fg.success">
              Your request has been successfully submitted. Please expect a few days of processing
              time before you receive confirmation.
            </Text>
          ) : backgroundCheckStatus === BackgroundCheckStatus['in-review'] ? (
            <Text color="fg.success">
              We have received your background check results and they are being reviewed. Please
              check back later.
            </Text>
          ) : canRequestBackgroundCheck === true ? (
            <BackgroundCheckInitiate countryCode={userCountry} onInitiated={handleInitiated} />
          ) : (
            <Text mb="16px">
              You are not eligible for a background check at Schoolhouse at the moment.
            </Text>
          )}
        </Box>
      )}
    />
  )
}
